import { useState, useEffect, ReactElement } from 'react'
import Page from '../Shared/Page'
import { adaloBackendAxios } from '../../utils/io/http/axios'
import './Dashboard.scss'
import Chart from './Chart'
import MultiValueChart from './MultiValueChart'
import NewRNAppsChart from './NewRNAppsChart'
import SimpleAdminPieChart, { type AdminPieDatum } from './SimpleAdminPieChart'

interface Stats {
  apps_count: number
  organizations_count: number
  users_count: number
}

interface ChartData {
  date: string
  count: number
}

interface SheetbridgeAnalytics {
  cumulative_by_day: { date: string; cumulative: number }[]
  daily_registrations_by_day: { date: string; count: number }[]
  users_by_bridge_bucket: { bucket: string; user_count: number }[]
  active_users_last_3_days: number
}

const EMPTY_SHEETBRIDGE_ANALYTICS: SheetbridgeAnalytics = {
  cumulative_by_day: [],
  daily_registrations_by_day: [],
  users_by_bridge_bucket: [],
  active_users_last_3_days: 0,
}

interface UsersResponse {
  date: string
  total_count: number
  confirmed_count: number
}

interface DbClusterStatResponse {
  host: string
  db_count: number
}

interface RnAppsAggregate {
  total_count: number
  new_rn_count: number
  old_rn_count: number
  not_defined_rn_count: number
}

const EMPTY_RN_AGG: RnAppsAggregate = {
  total_count: 0,
  new_rn_count: 0,
  old_rn_count: 0,
  not_defined_rn_count: 0,
}

const Dashboard = (): ReactElement => {
  const [stats, setStats] = useState<Stats | null>(null)
  const [dbClusterStats, setDbClusterStats] = useState<
    DbClusterStatResponse[] | null
  >(null)
  const [apps, setApps] = useState<ChartData[] | null>(null)
  const [users, setUsers] = useState<UsersResponse[] | null>(null)
  const [sheetbridgeAnalytics, setSheetbridgeAnalytics] =
    useState<SheetbridgeAnalytics | null>(null)
  const [organizations, setOrganizations] = useState<ChartData[] | null>(null)
  const [datasources, setDatasources] = useState<ChartData[] | null>(null)
  const [publishedAppsRN, setPublishedAppsRN] =
    useState<RnAppsAggregate>(EMPTY_RN_AGG)
  const [paidAppsRN, setPaidAppsRN] = useState<RnAppsAggregate>(EMPTY_RN_AGG)
  const [totalAppsRN, setTotalAppsRN] = useState<RnAppsAggregate>(EMPTY_RN_AGG)
  const [authProviderPie, setAuthProviderPie] = useState<AdminPieDatum[]>([])

  const fetchMetric = async <T,>(
    endpoint: string,
    setter: (data: T) => void
  ) => {
    try {
      const { data } = await adaloBackendAxios.get<T>(endpoint)
      setter(data)
    } catch (error) {
      console.error(`Failed to fetch data from ${endpoint}:`, error)
    }
  }

  /* eslint-disable @typescript-eslint/no-floating-promises */
  const fetchAllData = () => {
    fetchMetric('/admin/dashboard/stats', setStats)
    fetchMetric('/admin/dashboard/db-cluster-stats', setDbClusterStats)
    fetchMetric('/admin/dashboard/apps', setApps)
    fetchMetric('/admin/dashboard/users', setUsers)
    fetchMetric<SheetbridgeAnalytics>(
      '/admin/dashboard/sheetbridge-users',
      setSheetbridgeAnalytics
    )
    fetchMetric('/admin/dashboard/organizations', setOrganizations)
    fetchMetric('/admin/dashboard/datasources', setDatasources)
  }

  useEffect(() => {
    fetchAllData()

    const loadRnCharts = async () => {
      try {
        const [publishedAppsData, paidAppsData, totalAppsData, authRes] =
          await Promise.all([
            adaloBackendAxios.get<RnAppsAggregate>(
              '/admin/dashboard/new-rn-apps?chartType=published'
            ),
            adaloBackendAxios.get<RnAppsAggregate>(
              '/admin/dashboard/new-rn-apps?chartType=paid'
            ),
            adaloBackendAxios.get<RnAppsAggregate>(
              '/admin/dashboard/new-rn-apps?chartType=lastThirtyDays'
            ),
            adaloBackendAxios.get<AdminPieDatum[]>(
              '/admin/dashboard/auth-logins-30d'
            ),
          ])
        setPublishedAppsRN(publishedAppsData.data ?? EMPTY_RN_AGG)
        setPaidAppsRN(paidAppsData.data ?? EMPTY_RN_AGG)
        setTotalAppsRN(totalAppsData.data ?? EMPTY_RN_AGG)
        setAuthProviderPie(Array.isArray(authRes.data) ? authRes.data : [])
      } catch (error) {
        console.error('Failed to fetch RN apps dashboard charts:', error)
      }
    }

    loadRnCharts().catch(() => {
      /* errors are logged inside loadRnCharts */
    })
  }, [])

  return (
    <Page className="admin-dashboard-page" title="Dashboard">
      <div className="stats-container">
        <div>
          <p>Apps</p>
          <p>{stats?.apps_count || 0}</p>
        </div>
        <div>
          <p>Organizations</p>
          <p>{stats?.organizations_count || 0}</p>
        </div>
        <div>
          <p>Users</p>
          <p>{stats?.users_count || 0}</p>
        </div>
      </div>
      <div className="charts-container">
        <NewRNAppsChart data={publishedAppsRN} title="Published apps" />
        <NewRNAppsChart data={paidAppsRN} title="Paid apps" />
      </div>
      <div className="charts-container">
        <NewRNAppsChart
          data={totalAppsRN}
          title="Apps created in the last 30 days"
        />
        <SimpleAdminPieChart
          title="Sign-in provider (last 30 days)"
          desc="Distinct users with a sign-in or signup in the window, by last-used provider."
          data={authProviderPie}
        />
      </div>
      <div className="charts-container">
        <Chart
          title="Cluster Databases"
          data={(dbClusterStats || []).map(item => ({
            name: item.host,
            count: item.db_count,
          }))}
          xKey="name"
          yKey="count"
          barColor="#82ca9d"
        />
      </div>
      <div className="charts-container">
        <Chart
          title="New Daily Apps"
          desc="Last 30 days"
          data={apps}
          barColor="#ea6c57"
        />
        <Chart
          title="New Daily Organizations"
          desc="Last 30 days"
          data={organizations}
          barColor="#269d91"
        />
      </div>
      <div className="charts-container">
        <MultiValueChart
          title="New Daily Users"
          data={users ?? []}
          colors={[
            { dataKey: 'total_count', color: '#ea6c57' },
            { dataKey: 'confirmed_count', color: '#269d91' },
          ]}
          legendFormatter={value => {
            const labels: Record<string, string> = {
              total_count: 'Total',
              confirmed_count: 'Verified',
            }

            return labels[value] || value
          }}
          tooltipFormatter={(value, name) => {
            const labels: Record<string, string> = {
              total_count: 'Total',
              confirmed_count: 'Verified',
            }

            return [value.toString(), labels[name] || name]
          }}
        />
      </div>
      <div className="charts-container">
        <Chart
          title="Daily registered SheetBridge users"
          desc="New user accounts per day (last 30 days)"
          data={
            sheetbridgeAnalytics?.daily_registrations_by_day ??
            EMPTY_SHEETBRIDGE_ANALYTICS.daily_registrations_by_day
          }
          barColor="#5c9fd4"
        />
        <Chart
          title="Total SheetBridge sign-ups"
          desc="Cumulative registered users by day (last 30 days)"
          data={
            sheetbridgeAnalytics?.cumulative_by_day ??
            EMPTY_SHEETBRIDGE_ANALYTICS.cumulative_by_day
          }
          xKey="date"
          yKey="cumulative"
          barColor="#6b7fd7"
        />
      </div>
      <div className="charts-container">
        <Chart
          title="Users by bridge count"
          desc="Users grouped by how many bridges they own (max five per user)"
          data={
            sheetbridgeAnalytics?.users_by_bridge_bucket ??
            EMPTY_SHEETBRIDGE_ANALYTICS.users_by_bridge_bucket
          }
          xKey="bucket"
          yKey="user_count"
          barColor="#8874e8"
        />
        <div className="chart-container">
          <h2>Active SheetBridge users</h2>
          <p>
            Distinct users who own a bridge that received a public Bridge API
            request (GET/POST/etc.) in the last 3 days.
          </p>
          <p style={{ fontSize: 32, fontWeight: 600, marginTop: 8 }}>
            {sheetbridgeAnalytics?.active_users_last_3_days ??
              EMPTY_SHEETBRIDGE_ANALYTICS.active_users_last_3_days}
          </p>
        </div>
      </div>
      <div className="charts-container">
        <Chart
          title="New Daily Datasources"
          desc="Last 30 days"
          data={datasources}
          barColor="#f3a361"
        />
      </div>
    </Page>
  )
}

export default Dashboard
